import React from "react";
import { defineMessages, InjectedIntl } from "react-intl";
import { editContentPath } from "@constants/routing-constants";
import { UnresolvedLink } from "@stores/publishToLiveStore/publishToLiveStoreModel";

const messages = defineMessages({
  description: {
    id: "PublishActions.unresolvedLinksModal.description",
    defaultMessage: "The following contents have unresolved linked contents:",
  },
  linkedItem: {
    id: "PublishActions.unresolvedLinksModal.linkedItem",
    defaultMessage: 'Content "{linkedPublicId}" not found for locales: {locales}',
  },
});

interface GroupedSource {
  source: UnresolvedLink;
  linkedContents: Map<string, string[]>;
}

function groupUnresolvedLinks(unresolvedLinks: UnresolvedLink[]): Map<number, GroupedSource> {
  const grouped = new Map<number, GroupedSource>();
  for (const link of unresolvedLinks) {
    if (!grouped.has(link.sourceContentId)) {
      grouped.set(link.sourceContentId, { source: link, linkedContents: new Map() });
    }
    const group = grouped.get(link.sourceContentId)!;
    if (!group.linkedContents.has(link.linkedPublicId)) {
      group.linkedContents.set(link.linkedPublicId, []);
    }
    group.linkedContents.get(link.linkedPublicId)!.push(link.sourceContentLocale);
  }
  return grouped;
}

interface UnresolvedLinksModalContentProps {
  unresolvedLinks: UnresolvedLink[];
  spaceId: string;
  intl: InjectedIntl;
}

const UnresolvedLinksModalContent: React.FC<UnresolvedLinksModalContentProps> = ({
  unresolvedLinks,
  spaceId,
  intl,
}) => {
  const { formatMessage } = intl;
  const grouped = groupUnresolvedLinks(unresolvedLinks);

  return (
    <div>
      <p>{formatMessage(messages.description)}</p>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {Array.from(grouped.entries()).map(([contentId, { source, linkedContents }]) => (
          <li key={contentId} style={{ marginBottom: 12 }}>
            <a href={editContentPath(spaceId, contentId)} target="_blank" rel="noopener noreferrer">
              {window.location.origin + editContentPath(spaceId, contentId)}
            </a>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {Array.from(linkedContents.entries()).map(([linkedPublicId, locales]) => (
                <li key={linkedPublicId} style={{ marginBottom: 4 }}>
                  {formatMessage(messages.linkedItem, {
                    linkedPublicId,
                    locales: locales.join(", "),
                  })}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default UnresolvedLinksModalContent;
